/**
 * Service de conversion des créneaux horaires
 * Réutilise la logique de conversion de Gemini → Format interne
 */

export interface GeminiTimeSlot {
  start: string; // "10:00"
  end: string; // "11:00"
  dates?: string[]; // ["2025-10-29"] (optionnel)
}

export interface InternalTimeSlot {
  hour: number;
  minute: number;
  duration?: number; // en minutes
  enabled: boolean;
  label: string; // obligatoire pour compatibilité avec TimeSlot
}

/**
 * Convertit un créneau au format Gemini vers le format interne
 * Cette fonction réutilise la logique testée et validée de PollCreator
 */
export function convertGeminiSlotToInternal(
  geminiSlot: GeminiTimeSlot,
): InternalTimeSlot {
  // Calculer la durée en minutes entre start et end
  const startHour = parseInt(geminiSlot.start.split(":")[0]);
  const startMinute = parseInt(geminiSlot.start.split(":")[1]);
  const endHour = parseInt(geminiSlot.end.split(":")[0]);
  const endMinute = parseInt(geminiSlot.end.split(":")[1]);

  let durationMinutes =
    endHour * 60 + endMinute - (startHour * 60 + startMinute);
  // Plage qui traverse minuit (ex: 22h-00h, 23h30-01h) → durée sur le jour suivant
  if (durationMinutes <= 0) {
    durationMinutes += 24 * 60;
  }

  // Créer UN SEUL slot avec la durée complète
  return {
    hour: startHour,
    minute: startMinute,
    duration: durationMinutes,
    enabled: true,
    label: geminiSlot.start,
  };
}

/**
 * Convertit plusieurs créneaux Gemini vers le format timeSlotsByDate
 * CRÉE UN SEUL CRÉNEAU EXACT pour respecter la demande de l'IA
 */
export function convertGeminiSlotsToTimeSlotsByDate(
  geminiSlots: GeminiTimeSlot[],
  defaultDates: string[] = [],
  granularity: number = 30, // Granularité par défaut 30 min (non utilisée pour la conversion)
): Record<string, InternalTimeSlot[]> {
  const convertedTimeSlots: Record<string, InternalTimeSlot[]> = {};

  geminiSlots.forEach((slot) => {
    // Si le slot n'a pas de dates spécifiques, l'appliquer à toutes les dates par défaut
    const targetDates =
      slot.dates && slot.dates.length > 0 ? slot.dates : defaultDates;

    targetDates.forEach((date: string) => {
      if (!convertedTimeSlots[date]) {
        convertedTimeSlots[date] = [];
      }

      // Calculer les heures et minutes
      const startHour = parseInt(slot.start.split(":")[0]);
      const startMinute = parseInt(slot.start.split(":")[1]);
      const endHour = parseInt(slot.end.split(":")[0]);
      const endMinute = parseInt(slot.end.split(":")[1]);

      const startMinutes = startHour * 60 + startMinute;
      const endMinutes = endHour * 60 + endMinute;
      let durationMinutes = endMinutes - startMinutes;
      // Plage qui traverse minuit (ex: 22h-00h, 23h30-01h) → durée sur le jour suivant
      if (durationMinutes <= 0) {
        durationMinutes += 24 * 60;
      }

      // CRÉER UN SEUL CRÉNEAU avec la durée complète demandée par l'IA
      // La granularité n'est utilisée que pour l'affichage, pas pour diviser le créneau
      convertedTimeSlots[date].push({
        hour: startHour,
        minute: startMinute,
        duration: durationMinutes, // Durée complète (ex: 60 minutes pour 13h-14h)
        enabled: true,
        label: `${startHour.toString().padStart(2, "0")}:${startMinute.toString().padStart(2, "0")}`,
      });
    });
  });

  // Garde-fou (backstop) : si l'IA fragmente quand même une plage en créneaux
  // qui se chevauchent ou glissent (ex: 5 slots de 45min décalés de 15min sur
  // 12h-14h), on les fusionne en 1 seul bloc couvrant l'union. Les créneaux
  // réellement DISTINCTS (sans chevauchement) restent séparés.
  for (const date of Object.keys(convertedTimeSlots)) {
    convertedTimeSlots[date] = mergeOverlappingSlots(convertedTimeSlots[date]);
  }

  return convertedTimeSlots;
}

/**
 * Fusionne UNIQUEMENT les créneaux qui se CHEVAUCHENT (overlap strict).
 *
 * But : rattraper une fragmentation parasite de l'IA (créneaux glissants qui se
 * chevauchent, ex 12:00-12:45 + 12:15-13:00) sans casser un pavage VOULU
 * (créneaux bord-à-bord de durée fixe, ex 12:00-12:45 + 12:45-13:30 = tiling 45mn).
 *
 * Règle : on fusionne si sStart < curEnd (chevauchement vrai). On NE fusionne PAS
 * si sStart === curEnd (contigu bord-à-bord = tiling légitime, à préserver).
 */
export function mergeOverlappingSlots(
  slots: InternalTimeSlot[],
): InternalTimeSlot[] {
  if (slots.length <= 1) return slots;

  const sorted = [...slots].sort(
    (a, b) => a.hour * 60 + a.minute - (b.hour * 60 + b.minute),
  );

  const merged: InternalTimeSlot[] = [];
  let cur = { ...sorted[0] };
  let curEnd = cur.hour * 60 + cur.minute + (cur.duration ?? 0);

  for (let i = 1; i < sorted.length; i++) {
    const s = sorted[i];
    const sStart = s.hour * 60 + s.minute;
    const sEnd = sStart + (s.duration ?? 0);

    if (sStart < curEnd) {
      // Chevauchement STRICT → fragmentation parasite, étendre le bloc courant.
      if (sEnd > curEnd) {
        curEnd = sEnd;
        cur.duration = curEnd - (cur.hour * 60 + cur.minute);
      }
    } else {
      // sStart >= curEnd : contigu (tiling) ou disjoint → garder séparé.
      merged.push(cur);
      cur = { ...s };
      curEnd = sEnd;
    }
  }
  merged.push(cur);

  return merged;
}

/**
 * Calcule la granularité optimale basée sur les slots
 * Utilise le PGCD (Plus Grand Commun Diviseur) des minutes de début
 */
export function calculateOptimalGranularity(
  timeSlotsByDate: Record<string, InternalTimeSlot[]>,
): number {
  const allSlots = Object.values(timeSlotsByDate).flat();

  if (allSlots.length === 0) {
    return 60; // Défaut: 1 heure
  }

  // Fonction PGCD (Plus Grand Commun Diviseur)
  const gcd = (a: number, b: number): number => {
    return b === 0 ? a : gcd(b, a % b);
  };

  // Calculer le PGCD de toutes les minutes de début
  const minutes = allSlots.map((s) => s.minute);
  const minutesGCD = minutes.reduce((acc, minute) => gcd(acc, minute), 60);

  // Granularité optimale = PGCD des minutes (min 15min, max 60min)
  return Math.max(15, Math.min(60, minutesGCD));
}
